import { ROOMS, TOWN_LOCATIONS } from '../rooms';
import { VENUE_TYPES, instanceRoom } from '../venueTypes';

// THE VENUES THE TOWN VOTED FOR, client half (API city/VenueRegistry, plan:
// docs/VENUE_PIPELINE_PLAN.md). Khari, 2026-10-30: *"After approval, I would
// select the building type, and it would get the name the town voted for. It is
// placed where the system sees fit and added to the registry, becoming live in
// the world and available to artists, users and storylines."*
//
// Every venue in this World is keyed by ONE string — room id = venue key = city
// place slug = world_objects.ref — and until now every one of them was a
// constant (rooms.js ROOMS + TOWN_LOCATIONS, CityModel's VENUE_ORDER). A
// registry venue arrives at runtime instead, from GET /api/world/venues, and is
// written into the same two objects IN PLACE: ROOMS[slug] (its type's room with
// its own name and look) and a TOWN_LOCATIONS entry (the map, the markers, the
// venue tools). Nothing that reads them needs to know where an entry came from.
// CityModel takes the site straight from the store (worldStore.venueRegistry).
//
// The server never writes anchor or portal rows for these. Both are DERIVED
// here from the venue's `site` — the anchor at (x, z) turned to rotY, the door
// at site.door facing the street — and a hand-saved `venue_anchor` row for the
// same slug still beats the site, exactly as it beats the sampler.
//
// LIVE-SAFE: an endpoint that is missing, failing or empty registers nothing,
// and the World is exactly what it was. A slug that names a room this file did
// not create (a typo'd 'salon') is refused rather than allowed to replace it.
//
// Pure apart from the two objects it registers into — no store, no three — so
// it can be tested in node.

const SLUG = /^[a-z0-9][a-z0-9-]{1,63}$/;
const num = (v, d = null) => { const n = Number(v); return Number.isFinite(n) ? n : d; };
const r2 = (n) => Math.round(n * 100) / 100;

/** [x, z] in a site's own frame → world (three.js rotation.y, like cityMotion's turn). */
export function turn(v, th) {
  if (!th) return [v[0], v[1]];
  const c = Math.cos(th); const s = Math.sin(th);
  return [v[0] * c + v[1] * s, -v[0] * s + v[1] * c];
}

/**
 * One venue from the API, checked and filled in, or null. A site needs a
 * position; everything else has a fallback, because a venue missing its door
 * should still stand in the city rather than vanish.
 */
export function normalizeVenue(raw) {
  if (!raw || typeof raw.slug !== 'string' || !SLUG.test(raw.slug)) return null;
  const s = raw.site || {};
  const x = num(s.x); const z = num(s.z);
  if (x == null || z == null) return null;
  const rotY = num(s.rotY, 0);
  const w = Math.max(1, num(s.w, 8)); const d = Math.max(1, num(s.d, 8));
  // No door given: the middle of the street face, which is what the surveyor
  // would have picked for a building it had measured.
  const door = Array.isArray(s.door) && num(s.door[0]) != null && num(s.door[1]) != null
    ? [num(s.door[0]), num(s.door[1])]
    : (() => { const o = turn([0, d / 2], rotY); return [r2(x + o[0]), r2(z + o[1])]; })();
  const host = raw.host && Number.isFinite(Number(raw.host.avatarId))
    ? {
      avatarId: Number(raw.host.avatarId),
      name: String(raw.host.name || '').slice(0, 60),
      media: Array.isArray(raw.host.media) ? raw.host.media.filter((u) => typeof u === 'string' && /^https?:\/\//.test(u)).slice(0, 16) : [],
    }
    : null;
  const hm = (t) => (typeof t === 'string' && /^\d{1,2}:\d{2}$/.test(t) ? t : null);
  const hours = raw.hours && hm(raw.hours.from) && hm(raw.hours.to) ? { from: raw.hours.from, to: raw.hours.to } : null;
  return {
    slug: raw.slug,
    name: String(raw.name || raw.slug).trim().slice(0, 60) || raw.slug,
    type: typeof raw.type === 'string' ? raw.type : '',
    accent: typeof raw.accent === 'string' ? raw.accent : null,
    light: num(raw.light),
    hours,
    open: raw.open !== false,
    opensAt: num(raw.opensAt),
    closesAt: num(raw.closesAt),
    host,
    site: { kind: s.kind === 'lot' ? 'lot' : 'building', ref: typeof s.ref === 'string' ? s.ref : null, x, z, rotY, w, d, door },
  };
}

/**
 * Is it open at `now` (server ms)? The window the server sent decides, so a
 * venue opens and shuts on time between fetches: inside [opensAt, closesAt) is
 * open, before it or after it is shut (the next fetch brings the next window).
 * No hours means always open; no window falls back to the server's own `open`.
 */
export function venueOpenAt(v, now) {
  if (!v) return true;
  if (!v.hours) return v.open !== false;
  if (v.opensAt != null && v.closesAt != null) return now >= v.opensAt && now < v.closesAt;
  return v.open !== false;
}

/**
 * Where a venue's door is, and which way it faces, against its live anchor.
 * The door is stored in WORLD space on the site; it is carried into the site's
 * own frame and back out against whichever anchor is in force — so a venue an
 * admin moved or turned by hand (`ov`, a venue_anchor override) takes its door
 * with it instead of leaving it on the old building.
 */
export function doorPose(site, ov = null) {
  const local = turn([site.door[0] - site.x, site.door[1] - site.z], -(site.rotY || 0));
  const a = ov && Number.isFinite(ov.x) && Number.isFinite(ov.z)
    ? { x: ov.x, z: ov.z, rotY: Number(ov.rotY) || 0 }
    : { x: site.x, z: site.z, rotY: site.rotY || 0 };
  const o = turn(local, a.rotY);
  return { x: a.x + o[0], z: a.z + o[1], rotY: a.rotY };
}

/** A point `out` units in front of the door, on the street side: [x, z]. */
export function doorFront(site, ov = null, out = 2.2) {
  const p = doorPose(site, ov);
  return [p.x + Math.sin(p.rotY) * out, p.z + Math.cos(p.rotY) * out];
}

/**
 * The HUD's 0..1 map coordinate for a world point. Only read where a venue has
 * no live anchor (the old hand-built street); north up, like the others.
 */
const DEFAULT_BOUNDS = { min: [-120, -50.3], max: [120, 50.3] };
export function mapPosition(x, z, bounds = DEFAULT_BOUNDS) {
  const b = bounds?.min && bounds?.max ? bounds : DEFAULT_BOUNDS;
  const u = (x - b.min[0]) / ((b.max[0] - b.min[0]) || 1);
  const v = 1 - (z - b.min[1]) / ((b.max[1] - b.min[1]) || 1);
  return [r2(Math.min(0.98, Math.max(0.02, u))), r2(Math.min(0.98, Math.max(0.02, v)))];
}

/**
 * What changes the city's SHAPE — which venues stand where, under what name.
 * Hours and hosts change all day and must not re-measure the city.
 */
export function registryKey(list) {
  return (list || []).map((v) => [v.slug, v.type, v.name, v.accent, v.light, v.site.ref,
    r2(v.site.x), r2(v.site.z), r2(v.site.rotY), r2(v.site.door[0]), r2(v.site.door[1]), r2(v.site.w), r2(v.site.d)].join(':')).join('|');
}

// THE DOOR'S TRIGGER, in the portal's own frame (local +z = out to the street):
// a doorway wide, from just inside the face to a stride out onto the pavement.
// Thin is fine — Portals sweeps the path you walked, not the point you stopped.
export const DOOR_TRIGGER = { x: 0, y: 0.5, z: 0.5, w: 1.8, h: 2, d: 1.4 };

/**
 * The door of every registry venue, as a `portal` row Portals can fire —
 * derived, never saved (the server writes no portal rows for these). Skipped
 * for a venue somebody has already given a door by hand (⚙ → Portals): that one
 * is a decision, this is a default. `rows` are the saved portals.
 */
export function derivedPortals(registry, overrides, rows) {
  const doored = new Set((rows || []).filter((o) => o.zone_id === 'street')
    .map((o) => (o.data?.target?.kind === 'portal' ? null : (o.data?.target?.ref ?? o.ref))).filter(Boolean));
  const out = [];
  for (const slug of Object.keys(registry || {})) {
    const room = ROOMS[slug];
    if (!room?.registry || room.retired || doored.has(slug)) continue;
    const p = doorPose(registry[slug].site, overrides?.[slug]);
    out.push({
      object_id: `site:${slug}`, zone_id: 'street', kind: 'portal', ref: slug,
      pos: [p.x, 0, p.z], rotY: p.rotY, size: 1,
      data: { trigger: DOOR_TRIGGER, derived: true },
    });
  }
  return out;
}

const refused = new Set();

/**
 * Write the live venues into ROOMS and TOWN_LOCATIONS, in place. Returns what
 * happened, for the one log line. Venues no longer live leave the map and the
 * marker list; their ROOMS entry stays (marked retired) so somebody standing
 * inside one when it is retired is not left in a room that no longer exists.
 */
export function registerVenues(list, { bounds = null } = {}) {
  const res = { added: [], updated: [], removed: [], skipped: [] };
  const live = new Set();
  for (const v of list || []) {
    const type = VENUE_TYPES[v.type];
    const base = type && ROOMS[type.room];
    if (!type || !base) {
      if (!refused.has(`type:${v.slug}:${v.type}`)) { refused.add(`type:${v.slug}:${v.type}`); res.skipped.push(`${v.slug} (no building type "${v.type}" in this World)`); }
      continue;
    }
    if (ROOMS[v.slug] && !ROOMS[v.slug].registry) {
      if (!refused.has(`slug:${v.slug}`)) { refused.add(`slug:${v.slug}`); res.skipped.push(`${v.slug} (that id is a built-in room — not replaced)`); }
      continue;
    }
    const had = !!ROOMS[v.slug] && !ROOMS[v.slug].retired;
    ROOMS[v.slug] = instanceRoom(v.type, type, base, v);
    live.add(v.slug);

    const front = doorFront(v.site, null, 5);
    const entry = {
      key: v.slug, name: v.name, accent: ROOMS[v.slug].accent, interiorId: v.slug,
      world: [v.site.x, v.site.z], map: mapPosition(v.site.x, v.site.z, bounds),
      street: { pos: [r2(front[0]), 1.2, r2(front[1])], look: [v.site.door[0], 0.9, v.site.door[1]] },
      registry: true,
    };
    const at = TOWN_LOCATIONS.findIndex((l) => l.key === v.slug);
    if (at >= 0) {
      // Only ever OUR entry: a built-in key was refused above.
      Object.assign(TOWN_LOCATIONS[at], entry);
      res.updated.push(v.slug);
    } else {
      TOWN_LOCATIONS.push(entry);
      (had ? res.updated : res.added).push(v.slug);
    }
  }
  for (let i = TOWN_LOCATIONS.length - 1; i >= 0; i--) {
    const l = TOWN_LOCATIONS[i];
    if (!l.registry || live.has(l.key)) continue;
    TOWN_LOCATIONS.splice(i, 1);
    if (ROOMS[l.key]?.registry) ROOMS[l.key].retired = true;
    res.removed.push(l.key);
  }
  return res;
}
